const SVG_MIME_TYPE = 'image/svg+xml';

/**
 * The output is vector, but `<img>` uses the root width/height as its intrinsic size and gallery cards never
 * upscale past it (`max-w-full`/`max-h-full`), so small logos sat tiny inside large cards. That applies both to
 * width/height we fill in from a viewBox (e.g. 110×24) and to small explicit pixel sizes (e.g. `width="275"
 * height="48"`). 800 matches the mobile viewer's rendition size and keeps raster memory bounded.
 */
const MIN_PREVIEW_LONG_EDGE = 800;

// Physical and relative units (`pt`, `mm`, `em`, `%`) have no reliable pixel size to scale from, so only unitless
// and `px` lengths qualify.
const PIXEL_LENGTH_PATTERN = /^(\d+(?:\.\d+)?|\.\d+)(?:px)?$/i;

const FONT_FACE_RULE_PATTERN = /@font-face/i;

const parseViewBoxSize = (viewBox: string | null) => {
  if (!viewBox) {
    return undefined;
  }

  const values = viewBox
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const [minX, minY, width, height, ...extra] = values;
  const isValid = extra.length === 0 && [minX, minY, width, height].every(Number.isFinite) && width > 0 && height > 0;

  return isValid ? { width, height } : undefined;
};

// Only scales up: an authored viewBox larger than the minimum is already big enough to fill a card.
const scaleToMinPreviewSize = ({ width, height }: { width: number; height: number }) => {
  const scale = Math.max(1, MIN_PREVIEW_LONG_EDGE / Math.max(width, height));

  return { width: width * scale, height: height * scale };
};

// Scaling produces long float tails (e.g. 117.16052...) that add nothing visible to the attribute.
const formatSize = (value: number) => String(Number(value.toFixed(2)));

const parsePixelLength = (value: string | null) => {
  const match = value?.trim().match(PIXEL_LENGTH_PATTERN);
  const length = match ? Number(match[1]) : undefined;

  return length !== undefined && length > 0 ? length : undefined;
};

const scaleUpExplicitPixelSize = (root: Element) => {
  const width = parsePixelLength(root.getAttribute('width'));
  const height = parsePixelLength(root.getAttribute('height'));

  if (width === undefined || height === undefined || Math.max(width, height) >= MIN_PREVIEW_LONG_EDGE) {
    return;
  }

  // Without a viewBox, enlarging width/height would just enlarge the canvas and leave the art clipped at its
  // original size in the corner; a viewBox of the original size makes the art scale with it.
  if (!parseViewBoxSize(root.getAttribute('viewBox'))) {
    root.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  const previewSize = scaleToMinPreviewSize({ width, height });

  root.setAttribute('width', formatSize(previewSize.width));
  root.setAttribute('height', formatSize(previewSize.height));
};

const hasEmbeddedFonts = (root: Element) => {
  const styles = Array.from(root.getElementsByTagName('style'));

  return (
    styles.some((style) => FONT_FACE_RULE_PATTERN.test(style.textContent ?? '')) ||
    // SVG fonts; a selector list avoids the deprecated HTMLFontElement overload of getElementsByTagName('font').
    root.querySelector('font, font-face') !== null
  );
};

const isSingleNumber = (value: string) => {
  const trimmed = value.trim();

  return trimmed !== '' && !/[\s,]/.test(trimmed) && Number.isFinite(Number(trimmed));
};

const isIndentationTextNode = (node: Node) =>
  node.nodeType === node.TEXT_NODE && !node.textContent?.trim() && /[\n\r]/.test(node.textContent ?? '');

/**
 * Not a security sanitizer. Output is only safe as an `<img>` source (scripts and external resources don't run
 * there). Never inject it into the DOM.
 *
 * Viewers render the raw SVG, which breaks two common Illustrator export patterns:
 * - A viewBox-only root has no intrinsic size, so browsers fall back to 300px wide.
 * - Text is split into per-glyph `<tspan x=...>` runs positioned for the author's font. When the
 *   viewer lacks that font, the fallback font's metrics leave gaps ("En terprise"). Dropping the
 *   single-value `x` on same-line tspans (same `y` as the previous tspan) lets the text flow naturally.
 *   The source indentation (whitespace containing a line break) directly before each of those tspans
 *   is also removed, since it would otherwise render as a visible space once `x` is gone. All other
 *   whitespace, including authored spaces between tspans, is kept.
 *
 *   The offsets are kept only when the SVG embeds its fonts (an `@font-face` rule in a `<style>`, or a
 *   `<font>`/`<font-face>` element), since then they are exact and dropping them would lose kerning. We can't
 *   reliably know whether an `<img>`-rendered SVG will get the author's installed font, so otherwise they go.
 *
 * The width/height upscale applies in every case.
 */
export const normalizeSvgForImgPreview = (svgText: string): string | undefined => {
  const doc = new DOMParser().parseFromString(svgText, SVG_MIME_TYPE);
  const root = doc.documentElement;

  if (doc.getElementsByTagName('parsererror').length > 0 || root.localName !== 'svg') {
    return undefined;
  }

  if (!root.hasAttribute('width') || !root.hasAttribute('height')) {
    const viewBoxSize = parseViewBoxSize(root.getAttribute('viewBox'));

    if (viewBoxSize) {
      const previewSize = scaleToMinPreviewSize(viewBoxSize);

      root.setAttribute('width', formatSize(previewSize.width));
      root.setAttribute('height', formatSize(previewSize.height));
    }
  } else {
    scaleUpExplicitPixelSize(root);
  }

  const tspansToUnposition = Array.from(root.getElementsByTagName('text')).flatMap((textElement) => {
    const tspans = Array.from(textElement.children).filter((child) => child.localName === 'tspan');

    return tspans.filter((tspan, index) => {
      const previousY = index > 0 ? tspans[index - 1].getAttribute('y') : null;
      const y = tspan.getAttribute('y');
      const x = tspan.getAttribute('x');

      return y !== null && y === previousY && x !== null && isSingleNumber(x);
    });
  });

  if (!hasEmbeddedFonts(root)) {
    tspansToUnposition.forEach((tspan) => {
      tspan.removeAttribute('x');

      // Source indentation before a now-unpositioned tspan would render as a visible space.
      let previous = tspan.previousSibling;

      while (previous && isIndentationTextNode(previous)) {
        const next = previous.previousSibling;
        previous.remove();
        previous = next;
      }
    });
  }

  return new XMLSerializer().serializeToString(doc);
};
